<template>
  <RevContainer>
    <div class="mb-32 space-y-4 text-center">
      <h2 class="heading-1">{{ i18n(translations.title) }}</h2>

      <p class="body-1">{{ i18n(translations.subtitle) }}</p>
    </div>

    <BmProInfoBlock
      v-if="isBmProInfoBlockVisible"
      class="mb-32"
      name="bm_pro_info_block_cta"
      utm-source="bm_b2c_dashboard_my_orders"
      zone="my_orders"
    />

    <div
      v-if="!data && !error && !hasOrderlines"
      class="space-y-20 md:space-y-32"
      data-test="my-orders-initial-loading"
    >
      <RevSkeleton
        v-for="item in 6"
        :key="item"
        class="h-[540px] md:h-[340px]"
        shape="rectangle"
        width="100%"
      />
    </div>
    <div v-else-if="error" data-qa="order-list-error-block">
      <ErrorContainer @retry="onRetry" />
    </div>
    <div v-else>
      <div
        v-if="hasOrderlines"
        class="space-y-20 md:space-y-32"
        data-qa="order-list"
      >
        <OrderlineCard
          v-for="orderline in orderlines"
          :key="orderline.orderlineId"
          :orderline
        />
        <div v-if="canLoadMore" class="flex items-center justify-center">
          <LoadMore :is-loading="status === 'pending'" @load-more="loadMore" />
        </div>
      </div>
      <EmptyOrderlines v-else data-qa="empty-orderlines" />
    </div>

    <Intercom data-test="intercom" :manual="true" @ready="onIntercomReady" />
  </RevContainer>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted } from 'vue'

import LoadMore from '@backmarket/nuxt-layer-base/components/LoadMore/LoadMore.vue'
import Intercom from '@backmarket/nuxt-layer-care-platform/intercom/Intercom.vue'
import type { IntercomApi } from '@backmarket/nuxt-layer-care-platform/intercom/intercom.types'
import { useIntercom } from '@backmarket/nuxt-layer-care-platform/intercom/useIntercom'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevContainer } from '@ds/components/Container'
import { RevSkeleton } from '@ds/components/Skeleton'

import ErrorContainer from '@/scopes/care-commons/components/ErrorContainer/ErrorContainer.vue'
import OrderlineCard from '@/scopes/care-commons/components/order/OrderlineCard/OrderlineCard.vue'
import { useOrderlines } from '@/scopes/care-commons/composables/useOrderlines'
import BmProInfoBlock from '@/scopes/dashboard/components/BmProInfoBlock/BmProInfoBlock.vue'

import translations from './MyOrders.translations'
import EmptyOrderlines from './components/EmptyOrderlines/EmptyOrderlines.vue'
import { useBmProInfoBlockVisibility } from './composables/useBmProInfoBlockVisibility'

const { load } = useIntercom()
const i18n = useI18n()
const route = useRoute()
const { openSuccessToast } = useTheToast()

const { isVisible: isBmProInfoBlockVisible } = useBmProInfoBlockVisibility()

const { canLoadMore, loadMore, refresh, status, data, orderlines, error } =
  useOrderlines()

const hasOrderlines = computed(() => orderlines.value.length > 0)

function onRetry() {
  refresh()
}

onMounted(async () => {
  if (route.query.intercom_ticket_id) {
    load()
  }

  if (route.query.guest_upgrade === 'success') {
    openSuccessToast({
      title: i18n(translations.guestUpgradeSuccessTitle),
      content: i18n(translations.guestUpgradeSuccessContent),
    })

    const nextQuery = { ...route.query }
    delete nextQuery.guest_upgrade
    await navigateTo({ path: route.path, query: nextQuery }, { replace: true })
  }
})

const onIntercomReady = (intercom: IntercomApi) => {
  if (route.query.intercom_ticket_id) {
    intercom('showConversation', route.query.intercom_ticket_id.toString())
  }
}
</script>
